import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { type Location, useLocation } from "react-router-dom";
import { useClaims } from "../../../../../../auth/ClaimsProvider";
import { ShiOnePermission } from "../../../../../../auth/authorizationTypes";
import { useFeatureFlag } from "shared-ui/src/utils/FeatureFlagHelper";
import { Toast } from "shared-ui/src/components/Toast";
import { DrawerStackProvider } from "shared-ui/src/components/detail-drawer/drawer-stack";
import {
  RoutedOverlayBase,
  useRoutedDrawer,
} from "shared-ui/src/hooks/routedOverlays";
import { OrderDrawerContext } from "./OrderDrawerContext";
import { InvoiceDrawerReadyContext } from "./InvoiceDrawerReadyContext";
import {
  DrawerBaseReadinessProvider,
  ReadyDrawerBase,
  useDrawerAncestorsReady,
} from "./DrawerBaseReadiness";
import { OrderDrawer, getOrderNotFoundMessage } from "./OrderDrawer";
import { ORDER_DRAWER_ROUTES, ORDER_DRAWER_SEGMENT } from "./orderDrawerRoutes";
import { ORDER_DRAWER_DEFAULT_TAB } from "./orderDrawerSegments";
import { parseOrderRouteKey, toOrderRouteKey } from "./orderRouteKey";
import type { OrderDrawerIdentifier, OrderDrawerPreloadedData } from "./types";
import type { OrderRow } from "@features/orders-features/types/types";

// Loaded on first use so the data grid it needs stays out of every page's bundle.
const ReturnRequestDialog = lazy(() =>
  import("@features/orders-features/components/rma/ReturnRequestDialog").then(
    (module) => ({ default: module.ReturnRequestDialog }),
  ),
);

const ORDER_DRAWER_FEATURE_FLAG = "synergists.orders-details-drawer";

interface OrderDrawerProviderProps {
  children: ReactNode;
}

interface OrderDrawerEntry {
  identifier: OrderDrawerIdentifier;
  preloaded: OrderDrawerPreloadedData | null;
}

interface RoutedOrderDrawerEntry extends OrderDrawerEntry {
  key: string;
  /** Opened from the URL alone (deep link, refresh, Forward after close), so nothing was preloaded. */
  cold: boolean;
}

interface RenderedRouteEntry {
  entry: RoutedOrderDrawerEntry;
  path: string | undefined;
}

interface UnroutedDrawerState {
  entry: OrderDrawerEntry;
  open: boolean;
}

/**
 * Hosts the order drawer for every Customer Experience page. The drawer lives in the URL as
 * `{current page}/order/{orderKey}`, so Back closes it and refresh or a shared link reopens it.
 * Page URLs start with the customer's site prefix, which may equal a drawer segment.
 */
export function OrderDrawerProvider({ children }: OrderDrawerProviderProps) {
  const [readyPath, setReadyPath] = useState<string | null>(null);
  const invoiceDrawerReady = useMemo(
    () => ({ readyPath, setReadyPath }),
    [readyPath],
  );
  return (
    <InvoiceDrawerReadyContext.Provider value={invoiceDrawerReady}>
      <DrawerBaseReadinessProvider>
        <OrderDrawerStack>{children}</OrderDrawerStack>
      </DrawerBaseReadinessProvider>
    </InvoiceDrawerReadyContext.Provider>
  );
}

function OrderDrawerStack({ children }: OrderDrawerProviderProps) {
  const sitePrefix = useClaims().getClaimValue("sitePrefix");
  const areRouteAncestorsReady = useDrawerAncestorsReady();
  return (
    <DrawerStackProvider
      routes={ORDER_DRAWER_ROUTES}
      pathPrefix={sitePrefix}
      areRouteAncestorsReady={areRouteAncestorsReady}
    >
      <OrderDrawerHost>{children}</OrderDrawerHost>
    </DrawerStackProvider>
  );
}

const closeUnrouted = (prev: UnroutedDrawerState | null) =>
  prev?.open ? { ...prev, open: false } : prev;

// Must render inside DrawerStackProvider: the global drawer reads the routed chain it provides.
function OrderDrawerHost({ children }: OrderDrawerProviderProps) {
  const { hasPermission, claimsLoading } = useClaims();
  const { enabled: featureEnabled, loading: featureLoading } = useFeatureFlag(
    ORDER_DRAWER_FEATURE_FLAG,
    true,
  );

  const hasOrderViewPermission =
    hasPermission(ShiOnePermission.orderSearchMy) ||
    hasPermission(ShiOnePermission.orderSearchOrgUnit);

  // TODO: Remove `?? true` fallback once Synergist.enable-order-drawer is configured in App Configuration
  const isAvailable = (featureEnabled ?? true) && hasOrderViewPermission;
  const isAvailabilitySettled = !claimsLoading && !featureLoading;

  const {
    id: routeKey,
    isOpen: isRouteOpen,
    drawerPath,
    basePath,
    open: openRoute,
    close: closeRoute,
  } = useRoutedDrawer({ segment: ORDER_DRAWER_SEGMENT, mode: "global" });

  // In-app opens keep the caller's identifier and preloaded data; the URL only carries the key.
  const openedEntriesRef = useRef(new Map<string, OrderDrawerEntry>());
  // Stays mounted with open=false until its close animation ends, like the routed drawer.
  const [unrouted, setUnrouted] = useState<UnroutedDrawerState | null>(null);
  const isUnroutedOpen = !!unrouted?.open;
  const location = useLocation();
  // routeKey only changes once the navigation renders (possibly in a transition), so a
  // second open before then would push the same order again.
  const pendingOpenRef = useRef<{ key: string; from: Location } | undefined>(
    undefined,
  );

  const routeEntry = useMemo<RoutedOrderDrawerEntry | undefined>(() => {
    if (!routeKey) {
      return undefined;
    }
    const opened = openedEntriesRef.current.get(routeKey);
    if (opened) {
      return { ...opened, key: routeKey, cold: false };
    }
    const identifier = parseOrderRouteKey(routeKey);
    return identifier
      ? { identifier, preloaded: null, key: routeKey, cold: true }
      : undefined;
  }, [routeKey]);

  // Keep the last order mounted, at its own path, while the drawer animates closed. Derived
  // state rather than a render-time ref write, so a discarded render cannot leave it behind.
  const [lastRouteEntry, setLastRouteEntry] = useState<
    RenderedRouteEntry | undefined
  >(undefined);
  if (
    routeEntry &&
    (lastRouteEntry?.entry !== routeEntry || lastRouteEntry.path !== drawerPath)
  ) {
    setLastRouteEntry({ entry: routeEntry, path: drawerPath });
  }
  const renderedRouteEntry: RenderedRouteEntry | undefined = routeEntry
    ? { entry: routeEntry, path: drawerPath }
    : lastRouteEntry;

  useEffect(() => {
    if (!isRouteOpen) {
      openedEntriesRef.current.clear();
    }
  }, [isRouteOpen]);

  // One toast per invalid key, even when StrictMode runs the effect twice.
  const reportedInvalidKeyRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!routeKey) {
      reportedInvalidKeyRef.current = undefined;
      return;
    }
    if (routeEntry || reportedInvalidKeyRef.current === routeKey) {
      return;
    }
    reportedInvalidKeyRef.current = routeKey;
    Toast(getOrderNotFoundMessage(), "error");
    closeRoute();
  }, [routeKey, routeEntry, closeRoute]);

  useEffect(() => {
    if (isRouteOpen && isAvailabilitySettled && !isAvailable) {
      closeRoute();
    }
  }, [isRouteOpen, isAvailabilitySettled, isAvailable, closeRoute]);

  const openOrderDrawer = useCallback(
    (
      identifier: OrderDrawerIdentifier,
      preloaded?: OrderDrawerPreloadedData,
    ) => {
      if (!isAvailable) return;
      const entry = { identifier, preloaded: preloaded ?? null };
      const key = toOrderRouteKey(identifier);
      if (!key) {
        setUnrouted({ entry, open: true });
        return;
      }
      // useRoutedDrawer.open has no duplicate guard; a second open would push another entry.
      if (key === routeKey) return;
      const pending = pendingOpenRef.current;
      if (pending?.key === key && pending.from === location) return;
      pendingOpenRef.current = { key, from: location };
      openedEntriesRef.current.set(key, entry);
      setUnrouted(closeUnrouted);
      openRoute(key, { tab: ORDER_DRAWER_DEFAULT_TAB });
    },
    [isAvailable, routeKey, location, openRoute],
  );

  // One dialog for every entry point (orders grid, drawer, details page), hosted here so it
  // outlives the drawer that opened it and keeps its state while a drawer opens on top.
  const [returnRow, setReturnRow] = useState<OrderRow | null>(null);
  // The order drawer sits below the dialog by default; it is raised only once opened from the dialog.
  const [isDrawerRaised, setIsDrawerRaised] = useState(false);

  const openReturnRequest = useCallback((order: OrderRow) => {
    // Re-requesting the order already open (e.g. from a drawer raised over the dialog) keeps the
    // dialog's row stable so in-progress selections are not disturbed.
    setReturnRow((current) =>
      current?.biOrderId === order.biOrderId ? current : order,
    );
  }, []);

  const closeReturnRequest = useCallback(() => {
    setReturnRow(null);
    setIsDrawerRaised(false);
  }, []);

  const closeOrderDrawer = useCallback(() => {
    setUnrouted(closeUnrouted);
    closeRoute();
  }, [closeRoute]);

  const handleUnroutedDrawerExited = useCallback(() => {
    setUnrouted((prev) => (prev?.open ? prev : null));
    setIsDrawerRaised(false);
  }, []);

  const handleViewOrderFromReturnRequest = useCallback(
    (identifier: OrderDrawerIdentifier) => {
      setIsDrawerRaised(true);
      openOrderDrawer(identifier);
    },
    [openOrderDrawer],
  );

  const handleNotFound = useCallback(
    (salesOrder?: string) => {
      Toast(getOrderNotFoundMessage(salesOrder), "error");
      closeRoute();
    },
    [closeRoute],
  );

  const isRouteDrawerOpen = isRouteOpen && !!routeEntry;

  // Unmount the closed drawer so its order stream subscription stops.
  const handleRouteDrawerExited = useCallback(() => {
    if (isRouteDrawerOpen) return;
    setLastRouteEntry(undefined);
    setIsDrawerRaised(false);
  }, [isRouteDrawerOpen]);

  const contextValue = useMemo(
    () => ({
      openOrderDrawer,
      closeOrderDrawer,
      openReturnRequest,
      isOpen: isRouteDrawerOpen || isUnroutedOpen,
      isAvailable,
      basePath: isRouteDrawerOpen ? basePath : undefined,
    }),
    [
      openOrderDrawer,
      closeOrderDrawer,
      openReturnRequest,
      isRouteDrawerOpen,
      isUnroutedOpen,
      isAvailable,
      basePath,
    ],
  );

  return (
    <OrderDrawerContext.Provider value={contextValue}>
      {children}
      {isAvailable && renderedRouteEntry && (
        <RoutedOverlayBase path={renderedRouteEntry.path ?? null}>
          <ReadyDrawerBase
            path={isRouteDrawerOpen ? (renderedRouteEntry.path ?? null) : null}
          >
            <OrderDrawer
              key={renderedRouteEntry.entry.key}
              open={isRouteDrawerOpen}
              onClose={closeRoute}
              identifier={renderedRouteEntry.entry.identifier}
              preloadedData={renderedRouteEntry.entry.preloaded}
              routed
              raised={isDrawerRaised}
              onNotFound={
                renderedRouteEntry.entry.cold ? handleNotFound : undefined
              }
              onExited={handleRouteDrawerExited}
            />
          </ReadyDrawerBase>
        </RoutedOverlayBase>
      )}
      {isAvailable && unrouted && (
        <OrderDrawer
          open={unrouted.open}
          onClose={closeOrderDrawer}
          identifier={unrouted.entry.identifier}
          preloadedData={unrouted.entry.preloaded}
          raised={isDrawerRaised}
          onExited={handleUnroutedDrawerExited}
        />
      )}
      {returnRow && (
        <Suspense fallback={null}>
          <ReturnRequestDialog
            open
            row={returnRow}
            onClose={closeReturnRequest}
            onViewOrder={
              isAvailable ? handleViewOrderFromReturnRequest : undefined
            }
          />
        </Suspense>
      )}
    </OrderDrawerContext.Provider>
  );
}
